import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { getUserDisplayName } from '../../utils/userName';
import './UserDashboardShell.css';

const navItems = [
  {
    key: 'orders',
    labelKey: 'account.myOrders',
    to: '/user/dashboard',
    matches: ['/user/dashboard', '/user/orders'],
  },
  {
    key: 'wishlist',
    labelKey: 'account.wishlist',
    to: '/user/wishlist',
    matches: ['/user/wishlist'],
  },
  {
    key: 'payments',
    labelKey: 'account.paymentMethods',
    to: '/user/payments',
    matches: ['/user/payments'],
  },
  {
    key: 'addresses',
    labelKey: 'account.deliveryAddresses',
    to: '/user/addresses',
    matches: ['/user/addresses'],
  },
  {
    key: 'settings',
    labelKey: 'account.settings',
    to: '/user/settings',
    matches: ['/user/settings', '/user/profile'],
  },
];

const SidebarIcon = ({ type }) => {
  switch (type) {
    case 'orders':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 4.5 12 2l6 2.5v8L12 16 6 12.5Z" />
          <path d="M6 4.5 12 7l6-2.5" />
          <path d="M12 7v9" />
        </svg>
      );
    case 'wishlist':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 20.5s-7.5-4.4-7.5-10c0-2.8 2-4.8 4.7-4.8 1.8 0 3.2.8 4 2.1.8-1.3 2.2-2.1 4-2.1 2.7 0 4.8 2 4.8 4.8 0 5.6-7.5 10-7.5 10Z" />
        </svg>
      );
    case 'payments':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="4" y="5" width="16" height="14" rx="2.5" />
          <path d="M4 9.5h16" />
          <path d="M8 15h3.5" />
        </svg>
      );
    case 'addresses':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 21s6-5.7 6-10.7A6 6 0 0 0 6 10.3C6 15.3 12 21 12 21Z" />
          <circle cx="12" cy="10" r="1.9" />
        </svg>
      );
    case 'settings':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="3.2" />
          <path d="M12 3.5v2.4" />
          <path d="M12 18.1v2.4" />
          <path d="m5.9 5.9 1.7 1.7" />
          <path d="m16.4 16.4 1.7 1.7" />
          <path d="M3.5 12h2.4" />
          <path d="M18.1 12h2.4" />
          <path d="m5.9 18.1 1.7-1.7" />
          <path d="m16.4 7.6 1.7-1.7" />
        </svg>
      );
    case 'logout':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M10 4H5v16h5" />
          <path d="M14 8.5 18.5 12 14 15.5" />
          <path d="M18 12H9" />
        </svg>
      );
    default:
      return null;
  }
};

const UserDashboardShell = ({ children }) => {
  const { logout, user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const displayName = getUserDisplayName(user);

  const isNavItemActive = (item) => item.matches.some((path) => location.pathname.startsWith(path));

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <section className="account-shell">
      <header className="account-shell__header">
        <h1 className="account-shell__welcome">{t('account.greeting', { name: displayName })}</h1>
      </header>

      <div className="account-shell__divider" />

      <div className="account-shell__body">
        <aside className="account-shell__sidebar" aria-label="Account navigation">
          <nav className="account-shell__menu">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className={`account-shell__menu-item ${isNavItemActive(item) ? 'is-active' : ''}`}
              >
                <span className="account-shell__menu-icon">
                  <SidebarIcon type={item.key} />
                </span>
                <span>{t(item.labelKey)}</span>
              </Link>
            ))}

            <button type="button" className="account-shell__menu-item account-shell__menu-item--button" onClick={handleLogout}>
              <span className="account-shell__menu-icon">
                <SidebarIcon type="logout" />
              </span>
              <span>{t('account.logout')}</span>
            </button>
          </nav>
        </aside>

        <div className="account-shell__content">{children}</div>
      </div>
    </section>
  );
};

export default UserDashboardShell;
